/**
 * My Bank Account — self-service, view only. Edits require HR_PAYROLL_MANAGE and go through
 * the admin Employee Bank Accounts page — bank-detail changes are a common fraud vector.
 * Resolves the caller's own employeeId via GET /api/hr/employees/me (leaveApi.currentEmployee).
 */
import { Landmark } from "lucide-react";
import { leaveApi } from "../../leave/api";
import { payrollApi } from "../api";
import { useAsyncData } from "../hooks";
import { Card, CardHeader, PageHeader, PayrollError, PayrollLoading } from "../components/PayrollUI";

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-800">{value || <span className="text-slate-300">—</span>}</p>
    </div>
  );
}

export default function PayrollMyBankAccountPage() {
  const me = useAsyncData(() => leaveApi.currentEmployee(), []);
  const account = useAsyncData(
    () => (me.data ? payrollApi.getBankAccount(me.data.id) : Promise.resolve(null)),
    [me.data?.id],
  );

  const loading = me.loading || account.loading;
  const error = me.error || account.error;

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance"
        title="My Bank Account"
      />

      {loading && <PayrollLoading label="Loading your bank details…" />}
      {error && <PayrollError message={error} onRetry={() => { me.reload(); account.reload(); }} />}

      {!loading && !error && (
        <Card noPad>
          <CardHeader title="Bank Details" />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            <Field label="Bank Name" value={account.data?.bankName} />
            <Field label="Branch" value={account.data?.branchName} />
            <Field label="Account Name" value={account.data?.accountName} />
            <Field label="Account Number" value={account.data?.accountNumber} />
          </div>
          <div className="flex items-center gap-2 border-t px-5 py-4 text-xs text-slate-400">
            <Landmark className="h-4 w-4" />
            To correct these details, contact HR — bank-account edits require HR Payroll Manage access.
          </div>
        </Card>
      )}
    </div>
  );
}
